import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Search, FileText, Download, Eye, Trash2, Lock, Unlock, ChevronLeft, ChevronRight,
  Filter, Tag, SortDesc
} from 'lucide-react';
import {
  FaRegFilePdf, FaRegFileWord, FaRegFileExcel, FaRegFilePowerpoint, FaRegFileImage,
  FaRegFileLines, FaRegFileZipper, FaRegFileVideo, FaRegFileAudio, FaRegFile
} from 'react-icons/fa6';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const DocumentList = () => {
  const { user } = useAuth();
  const { categoryId: folderCategoryId } = useParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(folderCategoryId || '');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;

  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const isFolderView = !!folderCategoryId;

  const getDocumentIconMeta = (doc) => {
    const mime = (doc.mime_type || '').toLowerCase();
    const ext = (doc.file_extension || '').toLowerCase();

    const isPdf = mime === 'application/pdf' || ext === 'pdf';
    const isWord = mime.includes('word') || mime.includes('msword') || ext === 'doc' || ext === 'docx';
    const isExcel = mime.includes('excel') || mime.includes('spreadsheetml') || mime === 'text/csv' || ['xls', 'xlsx', 'csv'].includes(ext);
    const isPowerPoint = mime.includes('powerpoint') || mime.includes('presentationml') || ['ppt', 'pptx'].includes(ext);
    const isImage = mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    const isText = mime.startsWith('text/') || ext === 'txt';
    const isArchive = mime.includes('zip') || mime.includes('compressed') || mime.includes('rar') || mime.includes('7z') || ['zip', 'rar', '7z'].includes(ext);
    const isVideo = mime.startsWith('video/') || ['mp4', 'avi', 'mov'].includes(ext);
    const isAudio = mime.startsWith('audio/') || ['mp3', 'wav'].includes(ext);

    const color = '#6B7280';

    if (isPdf) return { Icon: FaRegFilePdf, color };
    if (isWord) return { Icon: FaRegFileWord, color };
    if (isExcel) return { Icon: FaRegFileExcel, color };
    if (isPowerPoint) return { Icon: FaRegFilePowerpoint, color };
    if (isImage) return { Icon: FaRegFileImage, color };
    if (isText) return { Icon: FaRegFileLines, color };
    if (isArchive) return { Icon: FaRegFileZipper, color };
    if (isVideo) return { Icon: FaRegFileVideo, color };
    if (isAudio) return { Icon: FaRegFileAudio, color };
    return { Icon: FaRegFile, color };
  };

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/documents/list', { params: { page, limit, search, categoryId, status } });
      setDocs(res.data.documents);
      setTotal(res.data.total);
    } catch (e) {}
    setLoading(false);
  }, [page, search, categoryId, status]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { api.get('/documents/categories').then(r => setCategories(r.data.categories)); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document permanently?')) return;
    try {
      await api.delete(`/documents/documents/${id}`);
      toast.success('Document deleted');
      fetchDocs();
    } catch (e) {}
  };

  const handleCheckout = async (id, isCheckedOut) => {
    try {
      if (isCheckedOut) {
        await api.post(`/documents/documents/${id}/checkin`);
        toast.success('Document checked in');
      } else {
        await api.post(`/documents/documents/${id}/checkout`);
        toast.success('Document checked out');
      }
      fetchDocs();
    } catch (e) {}
  };

  const handleDownload = async (id, title) => {
    try {
      const res = await api.get(`/documents/documents/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = title;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const handlePreview = async (id) => {
    try {
      const res = await api.get(`/documents/documents/${id}/view`, { responseType: 'blob' });
      const contentType = res.headers?.['content-type'] || res.data?.type || 'application/octet-stream';
      if (!res.data) {
        toast.error('Preview unavailable');
        return;
      }
      const blob = new Blob([res.data], { type: contentType });
      const objectUrl = window.URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      toast.error('Preview unavailable');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  useEffect(() => {
    setCategoryId(folderCategoryId || '');
    setPage(1);
  }, [folderCategoryId]);

  const folderCategory = isFolderView ? categories.find(c => c.id === folderCategoryId) : null;

  return (
    <div className="space-y-6">
      {isFolderView && (
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/app/categories')} className="btn-secondary !py-1.5 !px-3">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{folderCategory?.name || 'Category Folder'}</h1>
            <p className="text-gray-500 text-sm mt-1">Folder view</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          {!isFolderView && <h1 className="text-2xl font-bold text-gray-900">Documents</h1>}
          <p className="text-gray-500 text-sm mt-1">
            {isFolderView ? `${total} documents in ${folderCategory?.name || 'this folder'}` : `${total} documents`}
          </p>
        </div>
        {canEdit && (
          <Link to="/app/documents/upload" className="btn-primary">
            <Plus className="w-4 h-4" /> Upload
          </Link>
        )}
      </div>

      <div className="card !p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search documents..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select
          className="input w-auto"
          value={categoryId}
          onChange={e => { if (!isFolderView) { setCategoryId(e.target.value); setPage(1); } }}
          disabled={isFolderView}
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="input w-auto" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
      ) : docs.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No documents found</p>
          {canEdit && <Link to="/app/documents/upload" className="text-primary-600 text-sm hover:underline mt-2 block">Upload your first document</Link>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {docs.map(doc => {
            const { Icon, color } = getDocumentIconMeta(doc);
            return (
              <div key={doc.id} className="card !p-4 hover:shadow-md transition-shadow group">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <Icon size={22} color={color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/app/documents/${doc.id}`}
                      className="font-medium text-gray-900 hover:text-primary-600 text-sm line-clamp-2 leading-snug block">
                      {doc.title}
                    </Link>
                    <p className="text-xs text-gray-400 mt-1">
                      {doc.file_extension?.toUpperCase()} · {formatSize(doc.file_size)}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    doc.status === 'published' ? 'bg-green-100 text-green-700' :
                    doc.status === 'archived' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'
                  }`}>{doc.status}</span>
                  {doc.is_checked_out && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Checked Out
                    </span>
                  )}
                  {doc.category_name && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{doc.category_name}</span>
                  )}
                </div>

                {doc.tags?.length > 0 && (
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {doc.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">#{tag}</span>
                    ))}
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-400">{doc.created_by_name} · {format(new Date(doc.updated_at), 'MMM d')}</p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => (isFolderView ? handlePreview(doc.id) : navigate(`/app/documents/${doc.id}`))}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded" title="View">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDownload(doc.id, doc.original_filename)}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Download">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    {canEdit && (
                      <button onClick={() => handleCheckout(doc.id, doc.is_checked_out)}
                        className={`p-1.5 rounded ${doc.is_checked_out ? 'text-orange-500 hover:bg-orange-50' : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50'}`}
                        title={doc.is_checked_out ? 'Check In' : 'Check Out'}>
                        {doc.is_checked_out ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    {user?.role === 'admin' && (
                      <button onClick={() => handleDelete(doc.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page} of {totalPages} · {total} documents</p>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p-1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button disabled={page === totalPages} onClick={() => setPage(p => p+1)} className="btn-secondary !py-1.5 !px-3 disabled:opacity-40">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
